import { SelectedOption } from "@/core/export-image"
import clsx from "clsx"
import { memo, useCallback } from "react"
import { useTranslation } from "react-i18next"

interface ExportScopeSelectorProps {
	groupName: string
	selectedOption: SelectedOption
	setSelectedOption: (option: SelectedOption) => void
	selectedCount: number
	selectedExportableCount: number
	totalCount: number
	isProcessing: boolean
	allOptionAriaLabel?: string
}

interface ScopeRadioInputProps {
	groupName: string
	isChecked: boolean
	isDisabled?: boolean
	ariaLabel?: string
	onSelect: () => void
}

const ScopeRadioInput = memo(function ScopeRadioInput({
	groupName,
	isChecked,
	isDisabled,
	ariaLabel,
	onSelect,
}: ScopeRadioInputProps) {
	return (
		<input
			type="radio"
			name={groupName}
			aria-label={ariaLabel}
			checked={isChecked}
			disabled={isDisabled}
			onChange={onSelect}
		/>
	)
})

export const ExportScopeSelector = ({
	groupName,
	selectedOption,
	setSelectedOption,
	selectedCount,
	selectedExportableCount,
	totalCount,
	isProcessing,
}: ExportScopeSelectorProps) => {
	const { t } = useTranslation("components")
	const selectSelected = useCallback(() => {
		setSelectedOption("selected")
	}, [setSelectedOption])

	const selectAll = useCallback(() => {
		if (!isProcessing) {
			setSelectedOption("all")
		}
	}, [isProcessing, setSelectedOption])

	return (
		<div className="flex flex-col items-start space-y-2">
			<div className="flex w-full cursor-pointer flex-row space-x-1" onClick={selectSelected}>
				<ScopeRadioInput
					groupName={groupName}
					ariaLabel={t("exportScopeSelector.ariaSelectedPhotos")}
					isChecked={selectedOption == "selected"}
					onSelect={selectSelected}
				/>
				<div className="flex w-full text-sm">
					{selectedExportableCount !== selectedCount
						? t("exportScopeSelector.selectedImagesExportable", {
								exportableCount: selectedExportableCount,
								count: selectedCount,
							})
						: t("exportScopeSelector.selectedImages", { count: selectedCount })}
				</div>
			</div>

			<div
				className={clsx(
					isProcessing ? "opacity-50" : "cursor-pointer",
					"flex w-full flex-row space-x-1",
				)}
				onClick={selectAll}
			>
				<ScopeRadioInput
					groupName={groupName}
					ariaLabel={t("exportScopeSelector.ariaAllPhotos")}
					isChecked={selectedOption == "all"}
					isDisabled={isProcessing}
					onSelect={selectAll}
				/>
				<div
					className={clsx(
						isProcessing ? "text-secondary" : "text-primary",
						"flex w-full text-sm",
					)}
				>
					{t("exportScopeSelector.allImages", { totalCount }) +
						(isProcessing ? t("exportScopeSelector.stillProcessing") : "")}
				</div>
			</div>
		</div>
	)
}
